import React from 'react';
import Card from './Card';
import HandFan from './HandFan';
import './PokerTable.css';

export default function PokerTable({
  hands,
  discardPile,
  showDealDeck = false,
  hideDiscardCard = false,
  currentPlayerIndex,
  numPlayers,
  playerNames,
  gameScores,
  scoreFocusPlayerIndex = null,
  roundDeltaByPlayer = null,
  opponentPiles = null,
  opponentRoundDetails = null,
  revealedOpponentIndices = [],
  revealVisiblePileCounts = null,
  revealActiveOpponentIndex = null,
  currentTurnSecondsRemaining = null,
  quoteByPlayerIndex = {},
  discardTargetRef,
  onDiscardPileClick,
  onDiscardPilePointerDown,
  seatPileRefs,
  /** During deal: backs placed so far per player (same order as hands). */
  dealBackCounts = null,
  roundLabel = null,
}) {
  // User (index 0) is always bottom center. Other slots stay fixed by count.
  // Players are assigned to slots in clockwise order: P2 first on the left, then P3, P4, ...
  const slotByCount = {
    2: ['poker-table__seat--user', 'poker-table__seat--top-center'],
    3: ['poker-table__seat--user', 'poker-table__seat--top-1-4', 'poker-table__seat--top-3-4'],
    4: ['poker-table__seat--user', 'poker-table__seat--top-center', 'poker-table__seat--left-middle', 'poker-table__seat--right-middle'],
    5: ['poker-table__seat--user', 'poker-table__seat--top-1-4', 'poker-table__seat--top-3-4', 'poker-table__seat--left-middle', 'poker-table__seat--right-middle'],
  };
  const clockwiseOrder = [
    'poker-table__seat--left-middle',
    'poker-table__seat--top-1-4',
    'poker-table__seat--top-center',
    'poker-table__seat--top-3-4',
    'poker-table__seat--right-middle',
  ];
  const slotClasses = slotByCount[numPlayers] || slotByCount[2];
  const opponentClasses = slotClasses.slice(1);
  const sortedByClockwise = [...opponentClasses].sort(
    (a, b) => clockwiseOrder.indexOf(a) - clockwiseOrder.indexOf(b)
  );
  const seatPositions = Array.from({ length: numPlayers }, (_, index) => {
    const className = slotClasses[index] || 'poker-table__seat--top-center';
    const displayPlayerIndex =
      index === 0 ? 0 : sortedByClockwise.indexOf(className) + 1;
    return { index, className, displayPlayerIndex };
  });

  /** Side-by-side quote layout keeps bubbles toward table center so they are not clipped by the rim. */
  const quoteLayoutBySeatClass = {
    'poker-table__seat--left-middle': 'side-after',
    'poker-table__seat--right-middle': 'side-before',
    'poker-table__seat--top-1-4': 'side-after',
    'poker-table__seat--top-3-4': 'side-before',
    'poker-table__seat--top-center': 'stack-below',
  };

  return (
    <div className="poker-table">
      <div className="poker-table__inner" aria-hidden />
      <div className="poker-table__center">
        {roundLabel ? <div className="poker-table__round">{roundLabel}</div> : null}
        <div className="poker-table__discard" ref={discardTargetRef} onPointerDown={onDiscardPilePointerDown}>
          {showDealDeck ? (
            <div className="game-screen__deal-deck" aria-hidden>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`game-screen__deal-deck-layer game-screen__deal-deck-layer--${i}`}
                >
                  <Card
                    card={{ id: `deck-stack-${i}`, rank: 1, suit: 's', isJoker: false }}
                    faceUp={false}
                    disabled
                    plainBack={i < 3}
                  />
                </div>
              ))}
            </div>
          ) : discardPile ? (
            hideDiscardCard ? null : (
              <Card
                card={discardPile}
                faceUp
                selected={false}
                onClick={onDiscardPileClick}
                disabled={!onDiscardPileClick}
              />
            )
          ) : (
            <div className="poker-table__discard-placeholder" />
          )}
        </div>
      </div>
      <div className="poker-table__seats">
        {seatPositions.map(({ index, className, displayPlayerIndex }) => {
          const hand = hands[displayPlayerIndex] || [];
          const hasBackPlaceholders = hand.some((c) => String(c?.id || '').includes('-back-'));
          const isOpponentRevealed =
            displayPlayerIndex !== 0 &&
            Array.isArray(revealedOpponentIndices) &&
            revealedOpponentIndices.includes(displayPlayerIndex) &&
            !hasBackPlaceholders;
          const roundPileScores = opponentRoundDetails?.[displayPlayerIndex]?.pileScores;
          const revealPiles = isOpponentRevealed
            ? (() => {
                const fromRoundDetails = Array.isArray(roundPileScores)
                  ? roundPileScores
                      .filter((p) => Array.isArray(p?.cards) && p.cards.length > 0)
                      .map((p) => ({
                        cards: p.cards.filter(Boolean),
                        points: p.points ?? 0,
                        type: p.type || 'valid',
                        royal: Boolean(p.royal),
                      }))
                      .filter((p) => p.cards.length > 0)
                  : [];
                if (fromRoundDetails.length > 0) return fromRoundDetails;

                const rawPiles = opponentPiles?.[displayPlayerIndex] || [];
                const cleanedFallback = rawPiles
                  .filter((pile) => Array.isArray(pile))
                  .map((pile) => pile.filter(Boolean))
                  .filter((pile) => pile.length > 0)
                  .map((cards) => ({
                    cards,
                    points: 0,
                    type: 'valid',
                    royal: false,
                  }));
                if (cleanedFallback.length > 0) return cleanedFallback;
                const handFallback = hand.filter(Boolean);
                // If a player has no explicit piles, still reveal their cards as one stack.
                return handFallback.length > 0
                  ? [{ cards: handFallback, points: 0, type: 'valid', royal: false }]
                  : [];
              })()
            : [];
          const visiblePileCount =
            Number.isFinite(revealVisiblePileCounts?.[displayPlayerIndex])
              ? revealVisiblePileCounts[displayPlayerIndex]
              : revealPiles.length;
          const visibleRevealPiles = revealPiles.slice(0, Math.max(0, visiblePileCount));
          const hasRevealPiles = visibleRevealPiles.length > 0;
          const rawCount =
            dealBackCounts != null ? dealBackCounts[displayPlayerIndex] ?? 0 : hand.length;
          const opponentFanCards =
            displayPlayerIndex !== 0 && rawCount > 0
              ? dealBackCounts != null
                ? Array.from({ length: rawCount }, (_, i) => ({
                    id: `opp-${displayPlayerIndex}-deal-${i}`,
                    rank: 1,
                    suit: 's',
                    isJoker: false,
                  }))
                : hand
              : null;
          // During reveal, old opponent hand fan should disappear. We show piles instead.
          const displayCards = isOpponentRevealed ? null : opponentFanCards;
          const isCurrent = currentPlayerIndex === displayPlayerIndex;
          const isRevealActive = revealActiveOpponentIndex === displayPlayerIndex;
          const playerQuote =
            typeof quoteByPlayerIndex?.[displayPlayerIndex] === 'string'
              ? quoteByPlayerIndex[displayPlayerIndex]
              : null;
          const quoteLayout = quoteLayoutBySeatClass[className] || 'stack-below';
          const quoteBubbleClass =
            quoteLayout === 'side-after'
              ? 'poker-table__quote-bubble poker-table__quote-bubble--after'
              : quoteLayout === 'side-before'
                ? 'poker-table__quote-bubble poker-table__quote-bubble--before'
                : 'poker-table__quote-bubble poker-table__quote-bubble--below';
          return (
            <div
              key={index}
              className={`poker-table__seat ${className} ${isCurrent ? 'poker-table__seat--current' : ''}`}
            >
              {displayPlayerIndex !== 0 && (
                <div
                  className={`poker-table__seat-heading poker-table__seat-heading--quote-${quoteLayout}`}
                >
                  <div className="poker-table__seat-heading-main">
                    {quoteLayout === 'side-before' && playerQuote ? (
                      <div className={quoteBubbleClass} aria-live="polite">
                        {playerQuote}
                      </div>
                    ) : null}
                    <span className="poker-table__seat-label">
                      {playerNames[displayPlayerIndex]} {isCurrent && '(turn)'}
                      {isCurrent && Number.isFinite(currentTurnSecondsRemaining) && (
                        <span className="poker-table__turn-timer">{currentTurnSecondsRemaining}s</span>
                      )}
                    </span>
                    {quoteLayout === 'side-after' && playerQuote ? (
                      <div className={quoteBubbleClass} aria-live="polite">
                        {playerQuote}
                      </div>
                    ) : null}
                    {quoteLayout === 'stack-below' && playerQuote ? (
                      <div className={quoteBubbleClass} aria-live="polite">
                        {playerQuote}
                      </div>
                    ) : null}
                  </div>
                  {gameScores && gameScores[displayPlayerIndex] !== undefined && (
                    <>
                      <span
                        className={`poker-table__score${
                          scoreFocusPlayerIndex === displayPlayerIndex ? ' poker-table__score--surge-focus' : ''
                        }`}
                      >
                        Score: {gameScores[displayPlayerIndex]}
                      </span>
                      {scoreFocusPlayerIndex === displayPlayerIndex &&
                        Array.isArray(roundDeltaByPlayer) &&
                        roundDeltaByPlayer[displayPlayerIndex] !== undefined && (
                          <span className="poker-table__score-delta">
                            {roundDeltaByPlayer[displayPlayerIndex] > 0 ? '+' : ''}
                            {roundDeltaByPlayer[displayPlayerIndex]}
                          </span>
                        )}
                    </>
                  )}
                </div>
              )}
              <div
                className={`poker-table__pile ${displayCards ? 'poker-table__pile--fan' : ''}${
                  hasRevealPiles ? ' poker-table__pile--reveal-piles' : ''
                }${
                  isRevealActive ? ' poker-table__pile--reveal-active' : ''
                }`}
                ref={(el) => {
                  if (seatPileRefs?.current) seatPileRefs.current[index] = el;
                }}
              >
                {displayCards ? (
                  <HandFan
                    cards={displayCards}
                    interactive={false}
                    faceDown={!isOpponentRevealed}
                    compact
                  />
                ) : null}
                {hasRevealPiles ? (
                  <div className="poker-table__revealed-piles">
                    {visibleRevealPiles.map((pile, pileIndex) => {
                      const points = Number.isFinite(pile.points) ? pile.points : 0;
                      const ptsValid = points > 0;
                      return (
                        <div key={pileIndex} className="game-screen__pile poker-table__revealed-pile">
                          <div
                            className={`game-screen__pile-surge-score poker-table__revealed-pile-score ${
                              ptsValid
                                ? 'game-screen__pile-surge-score--ok'
                                : 'game-screen__pile-surge-score--bad'
                            }`}
                            aria-hidden
                          >
                            {points > 0 ? `+${points}` : `${points}`}
                          </div>
                          <div className="game-screen__pile-surge-inner">
                            {pile.cards.map((c, ci) => (
                              <div
                                key={c.id || `${pileIndex}-${ci}`}
                                className="game-screen__pile-card"
                                style={{ marginLeft: ci > 0 ? 'var(--user-pile-overlap, -52px)' : 0 }}
                              >
                                <Card card={c} faceUp disabled />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

